import { Util } from "@base/util/Util";
import { ILocaContext } from "@biz/language/LocaContext";
import { IsValidInputCheck, IsValidInputFn } from "@biz/validityChecks/ValidityCheckDefinitions";

export const MAX_COMMISSION_NAME = 32;

export const isEmpty: IsValidInputFn = (x) => {
  return !x
}

export const isEmptyCheck: IsValidInputCheck = (x, lc) => {
  if (isEmpty(x))
    return lc.f("InputValid_IsEmpty");
  return undefined;
}

export const isValidCommissionName: IsValidInputCheck = (x, lc) => {
  const min = 1;
  const max = MAX_COMMISSION_NAME;
  if (!x || x.length < min || x.length > max)
    return lc.f("InputValid_MinMax", { min, max })
  return undefined;
}

export const isValidVariantName: IsValidInputCheck = (x, lc) => {
  const min = 1;
  const max = MAX_COMMISSION_NAME;
  if (!x || x.length < min || x.length > max)
    return lc.f("InputValid_MinMax", { min, max })
  return undefined;
}

export const isValidEmailName: IsValidInputCheck = (x, lc) => {
  if (!Util.isValidEmail(x))
    return lc.f("InputValid_Email")
  return undefined;
}

export const isValidPassword: IsValidInputCheck = (x, lc) => {
  for (const c of [pwdNoSpaces, pwdLength(8, 20), pwdLowerCase, pwdUppercase, pwdSpecial]) {
    const ret = c(x, lc);
    if (ret)
      return ret;
  }
  return undefined;
}

export const pwdLowerCase: IsValidInputCheck = (x, lc) => {
  if (x) {
    for (const c of x) {
      if (isLowerCase(c))
        return undefined;
    }
  }
  return lc.f("InputValid_Password_LowerCase")
}

export const pwdNoSpaces: IsValidInputCheck = (x, lc) => {
  if (!!x && x.indexOf(" ") !== -1) {
    return lc.f("InputValid_Password_NoSpaces")
  }
  return undefined
}

export function pwdLength(min: number, max: number): IsValidInputCheck {
  return (val, lc) => {
    const length = val?.length || 0;
    if (length < min || length > max) {
      return lc.f("InputValid_Password_Length", { min, max })
    }

    return undefined
  }
}

function isLowerCase(str: string): boolean {
  return str === str.toLowerCase() && str !== str.toUpperCase();
}

function isUpperCase(str: string): boolean {
  return str === str.toUpperCase() && str !== str.toLowerCase();
}

export const pwdUppercase: IsValidInputCheck = (x, lc) => {
  if (x) {
    for (const c of x) {
      if (isUpperCase(c))
        return undefined;
    }
  }
  return lc.f("InputValid_Password_UpperCase")
}

function isCharNumber(c: string): boolean {
  return c >= '0' && c <= '9';
}

export const pwdDigit: IsValidInputCheck = (x, lc) => {
  if (x) {
    for (const c of x) {
      if (isCharNumber(c))
        return undefined;
    }
  }
  return lc.f("InputValid_Password_Digit")
}

export const pwdSpecial: IsValidInputCheck = (x, lc) => {
  if (x) {
    const format = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/

    if (format.test(x))
      return undefined;
  }
  return lc.f("InputValid_Password_Special")
}

export const isValidNumber: IsValidInputCheck = (x, lc) => {
  const v = +(x || "");
  if (Number.isNaN(v))
    return lc.f("InputValid_Number");
  return undefined;
}

export const isValidIntegerNumber: IsValidInputCheck = (x, lc) => {
  const v = +(x || "");
  if (Number.isNaN(v)) {
    return lc.f("InputValid_Number");
  }
  if (!Number.isInteger(v))
    return lc.f("InputValid_IntegerNumber");
  return undefined;
}

export function isValidRange(minValue: number, maxValue: number): IsValidInputCheck {
  return (x, lc) => isValidRangeHelper(x, minValue, maxValue, lc);
}

const isValidRangeHelper = (x: string | undefined, min: number, max: number, lc: ILocaContext): ReturnType<IsValidInputCheck> => {
  const v = +(x || "");
  if (!Number.isNaN(v)) {
    if (v < min || v > max)
      return lc.f("InputValid_Range", { min, max });
  }
  return undefined;
}

export function isValidGranularity(granularity: number): IsValidInputCheck {
  return (x, lc) => {
    const v = +(x || "");
    if (!Number.isNaN(v)) {
      const granDelta = v % granularity;
      if (granDelta !== 0)
        return lc.f("InputValid_Granularity", granularity);
    }
    return undefined;
  }
}